import { useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Flex,
  Input,
  Menu,
  MenuContent,
  MenuTrigger,
  Text,
  Textarea,
} from '@optiaxiom/react';
import { IconTrashCan } from '@optimizely/axiom-icons';

import { AgentParameter, AgentParameterType } from '@frontend/types/specialized-agent';

const PARAMETER_TYPES: Array<{ label: string; value: AgentParameterType }> = [
  { label: 'String', value: 'string' },
  { label: 'Number', value: 'number' },
  { label: 'Boolean', value: 'boolean' },
  { label: 'Datetime', value: 'datetime' },
  { label: 'Object', value: 'object' },
  { label: 'File', value: 'file' },
];

type ParameterFormProps = {
  initialValues?: AgentParameter<AgentParameterType>;
  onSave: (values: AgentParameter<AgentParameterType>) => void;
  onCancel: () => void;
  onDelete?: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingParameters?: Array<AgentParameter<AgentParameterType>>;
};

export function ParameterForm({
  initialValues,
  onSave,
  onCancel,
  onDelete,
  open,
  onOpenChange,
  existingParameters = [],
}: ParameterFormProps) {
  const editing = !!initialValues;
  const [parameter, setParameter] = useState<AgentParameter<AgentParameterType>>(
    initialValues ?? {
      name: '',
      description: '',
      required: false,
      type: 'string',
    }
  );

  const trimmedName = parameter.name.trim();
  const isDuplicate =
    trimmedName !== '' &&
    existingParameters.some(
      p => p.name.toLowerCase() === trimmedName.toLowerCase() && p.name !== initialValues?.name
    );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>{editing ? 'Edit Variable' : 'Add Variable'}</DialogHeader>
        <DialogBody>
          <Flex flexDirection="column" gap="16" style={{ minWidth: 400 }}>
            <Field label="Type" required info="Specify the data type of the variable">
              <Menu
                options={PARAMETER_TYPES.map(t => ({
                  label: t.label,
                  execute: () => setParameter({ ...parameter, type: t.value }),
                  selected: parameter.type === t.value,
                }))}
              >
                <MenuTrigger>
                  {PARAMETER_TYPES.find(t => t.value === parameter.type)?.label || 'Select type'}
                </MenuTrigger>
                <MenuContent />
              </Menu>
            </Field>
            <Field
              label="Name"
              required
              info="Specify the variable name to be referenced in the Prompt Template."
            >
              <Input
                placeholder="variable_name"
                value={parameter.name}
                h="40"
                onChange={e => setParameter({ ...parameter, name: e.target.value })}
              />
              {!parameter.name.trim() && (
                <Text color="fg.error" fontSize="sm">
                  * Required Field
                </Text>
              )}
              {isDuplicate && (
                <Text color="fg.error" fontSize="sm">
                  A variable with this name already exists
                </Text>
              )}
            </Field>
            <Field
              label="Description"
              info="Describe the variable's purpose and what it represents."
              required
            >
              <Textarea
                placeholder="Describe your variable"
                value={parameter.description ?? ''}
                onChange={e => setParameter({ ...parameter, description: e.target.value })}
              />
              {!parameter.description?.trim() && (
                <Text color="fg.error" fontSize="sm">
                  * Required Field
                </Text>
              )}
            </Field>
            <Flex flexDirection="row" alignItems="center" justifyContent="flex-start" gap="8">
              <Checkbox
                checked={parameter.required}
                onCheckedChange={() =>
                  setParameter({ ...parameter, required: !parameter.required })
                }
              />
              <Text>Required?</Text>
            </Flex>
          </Flex>
        </DialogBody>
        <DialogFooter>
          <Button appearance="default" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onDelete} icon={<IconTrashCan />} appearance="danger-outline" />
          <Button
            appearance="primary"
            onClick={() => onSave(parameter)}
            disabled={!parameter.name.trim() || !parameter.description?.trim() || isDuplicate}
          >
            {editing ? 'Save Changes' : 'Save Variable'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
